import { call, put, select, takeLatest, takeLeading } from '@redux-saga/core/effects';

import API, { BETA_API, rangeToAPI, selectedProductsToAPI } from '@sm/api';
import { AMAZON_SALES_CHANNEL_ID } from '@sm/SalesChannels';
import { axiosCall, isArray } from '@utils';
import {
  ANALYTICS_CHART_INTERVAL,
  DEFAULT_METRICS_LIST,
  TOTAL_ADS_METRIC_API_NAME
} from 'seller/pages/advertising/analytics/constants';
import { getCampaignExtendedRange } from 'seller/pages/advertising/analytics/utils';
import { setNotificationMsgAction, setSuccessMsgAction } from 'seller/store/actions';
import { productSearchSelector, selectorSelector, userSelector } from 'seller/store/reducers';
import {
  DELETE_USER_CUSTOM_AD_CAMPAIGN,
  PULL_ADVERTISING_CAMPAIGN_DETAILS,
  PULL_ADVERTISING_CAMPAIGNS,
  PULL_ANALYTICS_CHART_DATA,
  PULL_ASIN_KEYWORD_CHART_DATA,
  PULL_CUSTOM_CAMPAIGN_PRODUCTS_DETAILED_DATA,
  PULL_CUSTOM_CAMPAIGNS_DETAILED_DATA,
  PULL_USER_CUSTOM_AD_CAMPAIGNS,
  setAdvertisingCampaignDetailsAction,
  setAdvertisingCampaignsAction,
  setAdvertisingFetchingAction,
  setAnalyticsChartDataAction,
  setAsinKeywordChartDataAction,
  setCustomCampaignProductsDetailedDataAction,
  setCustomCampaignsDetailedDataAction,
  setUserCustomAdCampaignsAction,
  UPDATE_ADVERTISING_CAMPAIGN_DETAILS,
  UPLOAD_ASIN_KEYWORD_CHART_DATA,
  UPLOAD_USER_CUSTOM_AD_CAMPAIGNS
} from './action';

function* pullAdvertisingCampaignsSaga(action) {
  try {
    yield put(setAdvertisingFetchingAction({ campaigns: true }));
    if (!action?.skipCleanup) yield put(setAdvertisingCampaignsAction([]));
    const { marketplaceID } = yield select(selectorSelector);
    const params = { marketplaceID };
    let { data } = yield axiosCall.get(API.advertising.campaigns, { params });
    if (!isArray(data)) data = [];
    yield put(setAdvertisingCampaignsAction(data));
  } catch (e) {
    console.log('pullAdvertisingCampaignsSaga', e);
    yield put(setNotificationMsgAction('Pull advertising campaigns failed'));
  } finally {
    yield put(setAdvertisingFetchingAction({ campaigns: false }));
  }
}

function* pullAdvertisingCampaignDetailsSaga(action) {
  try {
    yield put(setAdvertisingFetchingAction({ campaigns: true }));
    const { data } = yield axiosCall.get(`${API.advertising.campaigns}${action.payload}/`);
    yield put(setAdvertisingCampaignDetailsAction(data));
  } catch (e) {
    console.log('pullAdvertisingCampaignDetailsSaga', e);
    yield put(setNotificationMsgAction('Pull advertising campaign details failed'));
  } finally {
    yield put(setAdvertisingFetchingAction({ campaigns: false }));
  }
}

function* updateAdvertisingCampaignDetailsSaga(action) {
  try {
    const { campaignDetails, products } = action.payload;
    yield put(setAdvertisingFetchingAction({ campaigns: true }));
    const patchData = { products: products.map(item => item.id) };
    yield axiosCall.patch(`${API.advertising.campaigns}${campaignDetails.id}/`, patchData);
    yield put(setSuccessMsgAction('Campaign products updated successfully'));
  } catch (e) {
    console.log('updateAdvertisingCampaignDetailsSaga', e);
    yield put(setNotificationMsgAction('Update advertising campaign products failed'));
  } finally {
    yield call(pullAdvertisingCampaignsSaga, { skipCleanup: true });
  }
}

function* pullUserCustomAdCampaignsSaga() {
  try {
    yield put(setAdvertisingFetchingAction({ customCampaigns: true }));
    const { data } = yield axiosCall.get(API.advertising.custom_campaigns);
    yield put(setUserCustomAdCampaignsAction(data?.campaigns || []));
  } catch (e) {
    console.log('pullUserCustomAdCampaignsSaga', e);
    yield put(setNotificationMsgAction('Pull user custom ad campaigns failed'));
  } finally {
    yield put(setAdvertisingFetchingAction({ customCampaigns: false }));
  }
}

function* uploadUserCustomAdCampaignsSaga({ payload }) {
  try {
    yield put(setAdvertisingFetchingAction({ customCampaigns: true }));
    const { data } = yield axiosCall.post(API.advertising.custom_campaigns, { data: payload });
    const { campaigns, error } = data;
    if (error) {
      yield put(
        setNotificationMsgAction(`Campaigns have been uploaded with errors: ${error}`, 'warning')
      );
    } else {
      yield put(setSuccessMsgAction('Campaigns have been uploaded successfully'));
    }
    yield put(setUserCustomAdCampaignsAction(campaigns || []));
  } catch (e) {
    console.log('uploadUserCustomAdCampaignsSaga', e);
    yield put(setNotificationMsgAction('Upload custom ad campaign failed'));
  } finally {
    yield put(setAdvertisingFetchingAction({ customCampaigns: false }));
  }
}

function* deleteUserCustomAdCampaignSaga(action) {
  try {
    yield put(setAdvertisingFetchingAction({ customCampaigns: true }));
    yield axiosCall.delete(API.advertising.custom_campaigns, {
      data: { id: action.payload }
    });
    yield put(setSuccessMsgAction('Campaign has been deleted successfully'));
    yield call(pullUserCustomAdCampaignsSaga);
  } catch (e) {
    console.log('deleteUserCustomAdCampaignSaga', e);
    yield put(setNotificationMsgAction('Delete custom ad campaign failed'));
  }
}

const pullChartMetric = async (url, params) => {
  const { data } = await axiosCall.get(url, { params });
  return { data, params };
};

function* pullAnalyticsChartDataSaga(action) {
  try {
    const { selectedMetrics, selectedCampaign: campaignData, parentView } = action.payload;
    if (!campaignData) return;
    yield put(setAdvertisingFetchingAction({ analyticsChart: true }));
    const campaignExtendedRange = getCampaignExtendedRange(campaignData);
    const campaignProducts = parentView ? campaignData.children : campaignData.products;
    const params = {
      ...rangeToAPI(campaignExtendedRange),
      interval: ANALYTICS_CHART_INTERVAL,
      sales_channels: AMAZON_SALES_CHANNEL_ID,
      marketplaceID: campaignData.products[0].marketplace,
      ...selectedProductsToAPI(campaignProducts),
      cached: 'true'
    };
    const { profile } = yield select(userSelector);
    const url = profile?.beta ? BETA_API.metrix.chart : API.metrix.chart;
    const requestsData = yield Promise.all(
      selectedMetrics.map(metric =>
        pullChartMetric(url, { metric, interval: ANALYTICS_CHART_INTERVAL, ...params })
      )
    );
    const result = {};
    requestsData.forEach(item => {
      const { data, params: _params } = item;
      const { metric } = _params;
      result[metric] = data.metrix;
    });
    yield put(setAnalyticsChartDataAction(result));
  } catch (e) {
    console.log('pullAnalyticsChartDataSaga', e);
    yield put(setNotificationMsgAction('Pull analytics chart data failed'));
  } finally {
    yield put(setAdvertisingFetchingAction({ analyticsChart: false }));
  }
}

const pullProductData = async (url, product, params, metricsToPull) => {
  const requestsData = await Promise.all(
    metricsToPull.map(metric =>
      pullChartMetric(metric === TOTAL_ADS_METRIC_API_NAME ? API.metrix.chart : url, {
        metric,
        ...params,
        ...selectedProductsToAPI([product])
      })
    )
  );
  const result = {};
  requestsData.forEach(item => {
    const { data, params: _params } = item;
    const { metric } = _params;
    result[metric] = data.metrix;
  });
  return { productId: product.id, data: result };
};

const pullCampaignDetailedData = async (campaignData, profile, selectedMetrics, parentView) => {
  const url = profile?.beta ? BETA_API.metrix.chart : API.metrix.chart;
  const campaignExtendedRange = getCampaignExtendedRange(campaignData);
  const params = {
    ...rangeToAPI(campaignExtendedRange),
    interval: ANALYTICS_CHART_INTERVAL,
    sales_channels: AMAZON_SALES_CHANNEL_ID,
    marketplaceID: campaignData.products[0].marketplace,
    cached: 'true'
  };

  const metricsToPull = [...new Set([...selectedMetrics, ...DEFAULT_METRICS_LIST])];
  const campaignProducts = parentView ? campaignData.children : campaignData.products;

  const requestsData = await Promise.all(
    metricsToPull.map(metric =>
      pullChartMetric(metric === TOTAL_ADS_METRIC_API_NAME ? API.metrix.chart : url, {
        metric,
        ...params,
        ...selectedProductsToAPI(campaignProducts)
      })
    )
  );
  const result = {};
  requestsData.forEach(item => {
    const { data, params: _params } = item;
    const { metric } = _params;
    result[metric] = data.metrix;
  });

  return { campaign_id: campaignData.id, data: result };
};

function* pullCustomCampaignsDetailedDataSaga(action) {
  try {
    yield put(setAdvertisingFetchingAction({ customCampaignsDetailedData: true }));
    const { customCampaigns, selectedMetrics, parentView } = action.payload;
    const { profile } = yield select(userSelector);
    const campaignsDetails = yield Promise.all(
      customCampaigns.map(campaign =>
        pullCampaignDetailedData(campaign, profile, selectedMetrics, parentView)
      )
    );
    const campaignsDataByCampaignId = {};
    campaignsDetails.forEach(item => {
      campaignsDataByCampaignId[item.campaign_id] = item.data;
    });
    yield put(setCustomCampaignsDetailedDataAction(campaignsDataByCampaignId));
  } catch (e) {
    console.log('pullCustomCampaignsDetailedDataSaga', e);
    yield put(setNotificationMsgAction('Pull custom campaigns detailed data failed'));
  } finally {
    yield put(setAdvertisingFetchingAction({ customCampaignsDetailedData: false }));
  }
}

function* pullCustomCampaignProductsDetailedDataSaga(action) {
  try {
    const { selectedCampaign: campaignData, selectedMetrics, parentView } = action.payload;
    if (!campaignData) return;
    yield put(setAdvertisingFetchingAction({ customCampaignProductsDetailedData: true }));
    const { profile } = yield select(userSelector);
    const url = profile?.beta ? BETA_API.metrix.chart : API.metrix.chart;
    const campaignExtendedRange = getCampaignExtendedRange(campaignData);
    const params = {
      ...rangeToAPI(campaignExtendedRange),
      interval: ANALYTICS_CHART_INTERVAL,
      sales_channels: AMAZON_SALES_CHANNEL_ID,
      marketplaceID: campaignData.products[0].marketplace,
      cached: 'true'
    };
    const metricsToPull = [...new Set([...selectedMetrics, ...DEFAULT_METRICS_LIST])];
    const campaignProducts = parentView ? campaignData.children : campaignData.products;
    const byProductData = {};
    const byProductResponses = yield Promise.all(
      campaignProducts.map(product => pullProductData(url, product, params, metricsToPull))
    );
    byProductResponses.forEach(item => {
      const { data, productId } = item;
      byProductData[productId] = data;
    });
    yield put(setCustomCampaignProductsDetailedDataAction(byProductData));
  } catch (e) {
    console.log('pullCustomCampaignsDetailedDataSaga', e);
    yield put(setNotificationMsgAction('Pull custom campaign products detailed data failed'));
  } finally {
    yield put(setAdvertisingFetchingAction({ customCampaignProductsDetailedData: false }));
  }
}

function* pullAsinKeywordChartDataSaga() {
  try {
    const { selectedProducts } = yield select(productSearchSelector);
    if (!selectedProducts.length) return;
    yield put(setAdvertisingFetchingAction({ asinKeywordChart: true }));
    yield new Promise(resolve => setTimeout(resolve, 500)); // to prevent redundant requests during filters appling
    const { dateRange } = yield select(selectorSelector);
    const { data } = yield axiosCall.post(API.advertising.asin_keyword_ranking, {
      asins: selectedProducts.map(item => item.asin),
      marketplaceID: selectedProducts[0]?.marketplace,
      ...rangeToAPI(dateRange)
    });
    yield put(setAsinKeywordChartDataAction(data));
  } catch (e) {
    console.log('pullAsinKeywordChartDataSaga', e);
    yield put(setNotificationMsgAction('Pull ASIN keyword chart data failed'));
  } finally {
    yield put(setAdvertisingFetchingAction({ asinKeywordChart: false }));
  }
}

function* uploadAsinKeywordChartDataSaga(action) {
  try {
    yield put(setAdvertisingFetchingAction({ asinKeywordChart: true }));
    yield axiosCall.patch(API.advertising.asin_keyword_ranking, {
      data: action.payload
    });
  } catch (e) {
    console.log('uploadAsinKeywordChartDataSaga', e);
    yield put(setNotificationMsgAction('Upload ASIN keyword chart data failed'));
  } finally {
    yield call(pullAsinKeywordChartDataSaga);
  }
}

export function* advertisingSagaWatcher() {
  yield takeLeading(PULL_ADVERTISING_CAMPAIGNS, pullAdvertisingCampaignsSaga);
  yield takeLeading(UPLOAD_USER_CUSTOM_AD_CAMPAIGNS, uploadUserCustomAdCampaignsSaga);
  yield takeLeading(PULL_USER_CUSTOM_AD_CAMPAIGNS, pullUserCustomAdCampaignsSaga);
  yield takeLatest(DELETE_USER_CUSTOM_AD_CAMPAIGN, deleteUserCustomAdCampaignSaga);
  yield takeLeading(PULL_ADVERTISING_CAMPAIGN_DETAILS, pullAdvertisingCampaignDetailsSaga);
  yield takeLatest(UPDATE_ADVERTISING_CAMPAIGN_DETAILS, updateAdvertisingCampaignDetailsSaga);
  yield takeLatest(PULL_ANALYTICS_CHART_DATA, pullAnalyticsChartDataSaga);
  yield takeLatest(PULL_ASIN_KEYWORD_CHART_DATA, pullAsinKeywordChartDataSaga);
  yield takeLatest(UPLOAD_ASIN_KEYWORD_CHART_DATA, uploadAsinKeywordChartDataSaga);
  yield takeLatest(PULL_CUSTOM_CAMPAIGNS_DETAILED_DATA, pullCustomCampaignsDetailedDataSaga);
  yield takeLatest(
    PULL_CUSTOM_CAMPAIGN_PRODUCTS_DETAILED_DATA,
    pullCustomCampaignProductsDetailedDataSaga
  );
}
